import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { addDays, toISO, startOfWeek, DOW_SHORT, DOW_LONG } from "@/components/operators/matrixDateUtils";
import { buildAllCellEvents, heatmapClass } from "@/components/operators/matrixCellEvents";
import { useMatrixDragDrop } from "@/components/operators/useMatrixDragDrop";
import { MatrixToolbar, MatrixLegend } from "@/components/operators/MatrixToolbar";
import { MatrixChip } from "@/components/operators/MatrixChip";

export function OperatorsMatrix({ operators, onReassigned }) {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const dnd = useMatrixDragDrop(onReassigned);

  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) {
      const d = addDays(weekStart, i);
      out.push({
        iso: toISO(d), date: d,
        dow: DOW_SHORT[d.getDay()], dowLong: DOW_LONG[d.getDay()],
        dayNum: d.getDate(), month: d.getMonth() + 1,
      });
    }
    return out;
  }, [weekStart]);

  const todayIso = toISO(new Date());
  const rows = operators.filter(op => op.role !== "OWNER" && op.is_active);
  const cellEvents = useMemo(
    () => buildAllCellEvents(rows, days.map(d => d.iso)),
    [rows, days]
  );

  const totalThisWeek = rows.reduce((s, op) => {
    const cells = cellEvents[op.id] || {};
    return s + Object.values(cells).reduce((acc, list) => acc + list.length, 0);
  }, 0);

  return (
    <div className="space-y-3" data-testid="operators-matrix">
      <MatrixToolbar
        weekStart={weekStart} setWeekStart={setWeekStart}
        totalEvents={totalThisWeek} totalRows={rows.length}
      />

      <div className="card-surface">
        <table className="w-full table-fixed border-collapse text-xs" data-testid="matrix-table">
          <colgroup>
            <col className="w-[120px]" />
            {days.map(d => <col key={d.iso} />)}
          </colgroup>
          <thead>
            <tr className="bg-zinc-50/80">
              <th className="border-b border-r border-zinc-200 px-2 py-2 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Operatore</th>
              {days.map(d => {
                const isToday = d.iso === todayIso;
                return (
                  <th key={d.iso} className={`border-b border-zinc-200 px-1 py-2 text-center font-semibold ${isToday ? "bg-blue-50/80 text-[#002FA7]" : "text-zinc-600"}`}>
                    <div className="text-[9px] uppercase tracking-wider">{d.dow}</div>
                    <div className="font-mono text-sm font-bold leading-tight">{d.dayNum}</div>
                    <div className="text-[8px] text-zinc-400 leading-tight">{String(d.month).padStart(2, "0")}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-zinc-400 py-8 text-sm">Nessun operatore disponibile.</td></tr>
            ) : rows.map(op => (
              <MatrixOperatorRow
                key={op.id}
                op={op} days={days} cellEvents={cellEvents[op.id] || {}}
                operators={operators} dnd={dnd}
                onReassigned={onReassigned}
                navigate={navigate}
              />
            ))}
          </tbody>
        </table>
      </div>

      <MatrixLegend />
    </div>
  );
}

function MatrixOperatorRow({ op, days, cellEvents, operators, dnd, onReassigned, navigate }) {
  return (
    <tr className="border-b border-zinc-100 last:border-b-0">
      <td className="border-r border-zinc-200 px-2 py-2 align-top" data-testid={`matrix-row-${op.id}`}>
        <div className="font-medium text-zinc-900 truncate text-[11px]" title={op.name || "Senza nome"}>{op.name || "Senza nome"}</div>
        <div className="text-[10px] text-zinc-500 truncate" title={op.role_name}>{op.role_name}</div>
      </td>
      {days.map(d => (
        <MatrixCell
          key={d.iso}
          op={op} day={d}
          events={cellEvents[d.iso] || []}
          operators={operators} dnd={dnd}
          onReassigned={onReassigned}
          navigate={navigate}
        />
      ))}
    </tr>
  );
}

function MatrixCell({ op, day, events, operators, dnd, onReassigned, navigate }) {
  const cellKey = `${op.id}-${day.iso}`;
  const isDropTarget = dnd.dragOverCell === cellKey;
  const heatBg = heatmapClass(events.length);
  return (
    <td
      className={`align-top px-1 py-1 transition-colors ${heatBg} ${isDropTarget ? "outline outline-2 outline-[#002FA7] outline-offset-[-2px] bg-blue-100/60" : ""}`}
      data-testid={`matrix-cell-${op.id}-${day.iso}`}
      onDragOver={(e) => dnd.handleDragOver(e, op.id, day.iso)}
      onDragLeave={() => dnd.handleDragLeaveCell(cellKey)}
      onDrop={(e) => dnd.handleDrop(e, op.id, day.iso)}
    >
      <div className="flex flex-col gap-1">
        {events.map((ev, i) => {
          const isDragging =
            dnd.draggingChip?.ev?.doc_id === ev.doc_id &&
            dnd.draggingChip?.ev?.eventType === ev.eventType &&
            dnd.draggingChip?.fromIso === day.iso;
          const chipKey = `${ev.doc_id}-${ev.eventType}`;
          return (
            <MatrixChip
              key={chipKey}
              ev={ev}
              operatorId={op.id} isoDate={day.iso} chipIndex={i}
              isDragging={isDragging}
              draggable
              onDragStart={(e) => dnd.handleDragStart(e, ev, op.id, day.iso)}
              onDragEnd={dnd.handleDragEnd}
              onClick={() => navigate(ev.navigateTo)}
              showReassign={ev.kind !== "release-due"}
              operators={operators}
              onReassigned={onReassigned}
            />
          );
        })}
        {events.length === 0 && <div className="h-1" aria-hidden="true" />}
      </div>
    </td>
  );
}
